import { className, type PersonGroup, type PersonSummary } from '@brimstone/shared';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  Chip,
  DataGrid,
  DataRow,
  EmptyState,
  KpiTile,
  PageLayout,
  Panel,
} from '../../components/ui';
import { MOODS, personHref, usePeople, usePerson, type MoodFilter } from '../../lib/people';
import { useCharacters } from '../../lib/roster';
import { formatAge, formatClock, formatDate, formatPlayed } from '../../lib/time';

const COLUMNS = 'minmax(0,1.6fr) 84px 64px 92px 120px minmax(0,1fr)';

const MOOD_CHIPS: { value: MoodFilter; label: string }[] = [
  { value: 'good', label: 'Good' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'bad', label: 'Bad' },
  { value: 'unnoted', label: 'No note' },
];

function Mood({ person }: { person: Pick<PersonSummary, 'note'> }) {
  if (!person.note) return <span className="font-mono text-label text-muted">—</span>;
  const m = MOODS[person.note.mood];
  return <span className={`font-mono text-label font-semibold ${m.className}`}>{m.word}</span>;
}

/** "Warrior 45", or what's known of it. */
function classLevel(p: Pick<PersonSummary, 'class' | 'level'>): string {
  return [p.class ? className(p.class) : null, p.level ?? null].filter(Boolean).join(' ');
}

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-label uppercase tracking-label text-muted">{label}</span>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
        {children}
      </div>
    </div>
  );
}

/** /people (M13): everyone the roster grouped with, and everyone noted in I Remember You. */
export function PeoplePage() {
  const [params, setParams] = useSearchParams();
  const filters = {
    q: params.get('q') ?? '',
    mood: (params.get('mood') as MoodFilter | null) ?? null,
    characterId: params.get('character'),
  };
  const q = usePeople(filters);
  const all = usePeople({ q: '', mood: null, characterId: null });
  const characters = useCharacters();
  const set = (key: 'q' | 'mood' | 'character', value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const everyone = all.data?.people ?? [];
  const met = new Set(everyone.flatMap((p) => p.characters.map((c) => c.id)));
  const withChars = (characters.data?.characters ?? []).filter((c) => met.has(c.id));
  const people = q.data?.people;

  return (
    <PageLayout
      kpis={
        <>
          <KpiTile
            label="People met"
            value={all.data ? everyone.filter((p) => p.groups).length : '—'}
          />
          <KpiTile label="Noted" value={all.data ? everyone.filter((p) => p.note).length : '—'} />
          <KpiTile
            label="Groups"
            value={all.data ? everyone.reduce((s, p) => s + p.groups, 0) : '—'}
          />
          <KpiTile
            label="Time grouped"
            value={
              all.data ? formatPlayed(everyone.reduce((s, p) => s + p.secondsTogether, 0)) : '—'
            }
          />
        </>
      }
      main={
        <Panel title="People" meta={people ? `${people.length} PEOPLE` : undefined}>
          <div className="flex flex-col gap-3 border-b border-line px-4 py-3">
            <label className="flex flex-wrap items-center gap-3">
              <span className="text-label uppercase tracking-label text-muted">Search</span>
              <input
                type="search"
                value={filters.q}
                placeholder="Name or note"
                onChange={(e) => set('q', e.target.value || null)}
                className="min-h-11 min-w-0 flex-1 border border-line bg-bg px-3 text-compact"
              />
            </label>
            <FilterRow label="Note">
              <Chip pressed={filters.mood === null} onClick={() => set('mood', null)}>
                All
              </Chip>
              {MOOD_CHIPS.map((m) => (
                <Chip
                  key={m.value}
                  pressed={filters.mood === m.value}
                  onClick={() => set('mood', filters.mood === m.value ? null : m.value)}
                >
                  {m.label}
                </Chip>
              ))}
            </FilterRow>
            {withChars.length > 0 && (
              <FilterRow label="Grouped with">
                <Chip pressed={!filters.characterId} onClick={() => set('character', null)}>
                  Anyone
                </Chip>
                {withChars.map((c) => (
                  <Chip
                    key={c.id}
                    pressed={filters.characterId === c.id}
                    onClick={() => set('character', filters.characterId === c.id ? null : c.id)}
                  >
                    {c.name}
                  </Chip>
                ))}
              </FilterRow>
            )}
          </div>
          {!people ? (
            <EmptyState>{q.isError ? 'Couldn’t load the people.' : 'Loading…'}</EmptyState>
          ) : people.length === 0 ? (
            <EmptyState>
              {everyone.length
                ? 'Nobody matches these filters.'
                : 'Nobody yet — groups appear after a logout with the Brimstone addon 0.4.0, notes after I Remember You’s file is uploaded.'}
            </EmptyState>
          ) : (
            <DataGrid
              label="People"
              columns={COLUMNS}
              header={['Player', 'Note', 'Groups', 'Together', 'Last met', 'With']}
            >
              {people.map((p) => (
                <DataRow key={p.name} columns={COLUMNS}>
                  <span role="cell" className="min-w-0">
                    <Link to={personHref(p.name)} className="text-name">
                      {p.name}
                    </Link>{' '}
                    <span className="text-secondary text-muted">{classLevel(p)}</span>
                    {p.note?.text && (
                      <span className="block truncate text-secondary text-muted">
                        {p.note.text}
                      </span>
                    )}
                  </span>
                  <span role="cell">
                    <Mood person={p} />
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    {p.groups || '—'}
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    {p.secondsTogether ? formatPlayed(p.secondsTogether) : '—'}
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    {p.lastMet ? (
                      formatClock(p.lastMet)
                    ) : (
                      <span className="text-muted">never grouped</span>
                    )}
                  </span>
                  <span role="cell" className="truncate text-secondary">
                    {p.characters.map((c) => c.name).join(', ') || (
                      <span className="text-muted">—</span>
                    )}
                  </span>
                </DataRow>
              ))}
            </DataGrid>
          )}
        </Panel>
      }
      side={
        <Panel title="Sources" level="side">
          <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 px-4 py-3 text-compact">
            <dt className="text-muted">Groups</dt>
            <dd className="m-0">
              Brimstone addon{' '}
              <span className="font-mono text-muted">
                {all.data?.groupsAsOf ? `as of ${formatAge(all.data.groupsAsOf)}` : 'NO DATA'}
              </span>
            </dd>
            <dt className="text-muted">Notes</dt>
            <dd className="m-0">
              I Remember You{' '}
              <span className="font-mono text-muted">
                {all.data?.notesAsOf ? `as of ${formatAge(all.data.notesAsOf)}` : 'NO DATA'}
              </span>
            </dd>
          </dl>
          <p className="m-0 border-t border-line px-4 py-3 text-secondary text-muted">
            Everyone in a party or raid is recorded on its own. Notes and moods are the ones you
            give in game: right-click a player and choose Remember.
          </p>
        </Panel>
      }
    />
  );
}

function GroupRow({ g }: { g: PersonGroup }) {
  const lines = [
    ...g.cleared.map((c) => `Cleared ${c.dungeon}`),
    ...g.bossKills.map((k) => `Killed ${k.encounter}${k.instance ? ` (${k.instance})` : ''}`),
    ...g.deaths.map((d) => d.text),
  ];
  return (
    <li className="flex flex-col gap-1 border-b border-line-soft px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span>
          <Link to={`/book/${g.characterId}`}>{g.characterName}</Link>{' '}
          <span className="text-secondary text-muted">
            {g.kind === 'raid' ? 'raid' : 'party'} · {formatPlayed(g.seconds)}
          </span>
        </span>
        <span className="font-mono text-secondary text-muted">
          {formatDate(g.from)} {formatClock(g.from).slice(-5)}
        </span>
      </div>
      {g.others.length > 0 && (
        <span className="text-secondary text-muted">
          With{' '}
          {g.others.map((o, i) => (
            <span key={o}>
              {i > 0 && ', '}
              <Link to={personHref(o)}>{o}</Link>
            </span>
          ))}
        </span>
      )}
      {lines.length > 0 && (
        <ul className="m-0 list-none p-0 text-secondary">
          {lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** /people/:name (M13): one person's note and every group with them. */
export function PersonPage() {
  const { name = '' } = useParams();
  const q = usePerson(name);
  const p = q.data?.person;
  return (
    <PageLayout
      kpis={
        <>
          <KpiTile label="Groups" value={p ? p.groups : '—'} />
          <KpiTile label="Together" value={p ? formatPlayed(p.secondsTogether) : '—'} />
          <KpiTile label="Dungeons cleared" value={p ? p.dungeonsCleared : '—'} />
          <KpiTile label="Bosses killed" value={p ? p.bossKills : '—'} />
        </>
      }
      main={
        <>
          <Panel
            title={p?.name ?? name}
            meta={p ? classLevel(p).toUpperCase() || undefined : undefined}
          >
            {!p ? (
              <EmptyState>
                {q.isError ? (
                  <>
                    Nobody by that name. <Link to="/people">All people</Link>
                  </>
                ) : (
                  'Loading…'
                )}
              </EmptyState>
            ) : p.note ? (
              <div className="flex flex-col gap-1 px-4 py-3">
                <Mood person={p} />
                <p className="m-0">{p.note.text || <span className="text-muted">No text.</span>}</p>
                {p.note.notedAt && (
                  <span className="font-mono text-label text-muted">
                    Noted {formatDate(p.note.notedAt)}
                  </span>
                )}
              </div>
            ) : (
              <EmptyState>No note — right-click them in game and choose Remember.</EmptyState>
            )}
          </Panel>
          {p && (
            <Panel title="Groups" meta={`${q.data!.groups.length}`}>
              {q.data!.groups.length === 0 ? (
                <EmptyState>Never grouped with a roster character.</EmptyState>
              ) : (
                <ol className="m-0 list-none p-0">
                  {q.data!.groups.map((g) => (
                    <GroupRow key={`${g.characterId}${g.from}`} g={g} />
                  ))}
                </ol>
              )}
            </Panel>
          )}
        </>
      }
      side={
        p && (
          <Panel title="Met by" level="side">
            {p.characters.length === 0 ? (
              <EmptyState>Nobody on the roster yet.</EmptyState>
            ) : (
              <ul className="m-0 list-none px-4 py-2 text-compact">
                {p.characters.map((c) => (
                  <li
                    key={c.id}
                    className="flex justify-between gap-3 border-b border-line-soft py-2 last:border-b-0"
                  >
                    <Link to={`/book/${c.id}`}>{c.name}</Link>
                    <span className="font-mono text-muted">
                      {c.groups} {c.groups === 1 ? 'group' : 'groups'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {p.firstMet && (
              <p className="m-0 border-t border-line px-4 py-3 font-mono text-label text-muted">
                First met {formatDate(p.firstMet)} · last {formatDate(p.lastMet!)}
              </p>
            )}
          </Panel>
        )
      }
    />
  );
}

/** A Book page's "Grouped with": who this character groups with most (M13). */
export function GroupedWithPanel({ characterId, name }: { characterId: string; name: string }) {
  const q = usePeople({ q: '', mood: null, characterId });
  const top = (q.data?.people ?? [])
    .map((p) => ({ p, n: p.characters.find((c) => c.id === characterId)?.groups ?? 0 }))
    .sort((a, b) => b.n - a.n || (a.p.name < b.p.name ? -1 : 1))
    .slice(0, 5);
  return (
    <Panel title="Grouped with" level="side">
      {!q.data ? (
        <EmptyState>{q.isError ? 'Couldn’t load it.' : 'Loading…'}</EmptyState>
      ) : top.length === 0 ? (
        <EmptyState>Nobody yet — {name}’s groups appear after a logout.</EmptyState>
      ) : (
        <ul className="m-0 list-none px-4 py-2 text-compact">
          {top.map(({ p, n }) => (
            <li
              key={p.name}
              className="flex items-baseline justify-between gap-3 border-b border-line-soft py-2 last:border-b-0"
            >
              <span className="min-w-0 truncate">
                <Link to={personHref(p.name)}>{p.name}</Link> <Mood person={p} />
              </span>
              <span className="shrink-0 font-mono text-muted">
                {n} {n === 1 ? 'group' : 'groups'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
